import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import API, { parseError } from "../utils/api";
import { Color, Role } from "../constants";

function Badge({ label, bg, color }) {
  return (
    <span
      className="inline-block px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ backgroundColor: bg, color }}
    >
      {label}
    </span>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.4)" }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-gray-800 text-lg">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl"
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const inputCls =
  "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-200";
const selectCls = inputCls;

export default function Delegations() {
  const { user } = useAuth();
  const isDSM = user?.role === Role.DSM;

  const [delegations, setDelegations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [error, setError] = useState("");
  const [asms, setAsms] = useState([]);
  const [dsms, setDsms] = useState([]);
  const [form, setForm] = useState({
    delegated_to_id: "",
    dsm_id: "",
    reason: "",
    expires_at: "",
  });

  useEffect(() => {
    fetchDelegations();
    // API.get("/admin/staff", { params: { is_active: true } })
    //   .then((r) => {
    //     // setAsms(r.data.filter((s) => s.role === Role.ASM));
    //     const allAsms = r.data.filter((s) => s.role === Role.ASM);
    //     setAsms(
    //       user?.role === Role.DSM
    //         ? allAsms.filter((a) => a.manager_id === user.id)
    //         : allAsms
    //     );
    //     setDsms(r.data.filter((s) => s.role === Role.DSM));
    //     // Pre-fill DSM field if current user is DSM
    //     if (user?.role === Role.DSM) {
    //       setForm((f) => ({ ...f, dsm_id: user.id }));
    //     }
    //   })
    //   .catch(() => {});
    API.get("/lookup/asms")
      .then((r) => setAsms(r.data))
      .catch(() => {});

    API.get("/lookup/dsms")
      .then((r) => setDsms(r.data))
      .catch(() => {});

    if (user?.role === Role.DSM) {
      setForm((f) => ({ ...f, dsm_id: user.id }));
    }
  }, []);

  const fetchDelegations = () => {
    setLoading(true);
    API.get("/admin/delegations", { params: { active_only: false } })
      .then((r) => setDelegations(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const handleCreate = async () => {
    setError("");
    try {
      await API.post("/admin/delegations", {
        ...form,
        expires_at: form.expires_at + "T23:59:59",
      });
      setShowModal(false);
      fetchDelegations();
    } catch (err) {
      setError(parseError(err, "Failed to create delegation."));
    }
  };

  const confirmRevoke = (d) => {
    setRevokeTarget(d);
    setShowRevokeConfirm(true);
  };

  const handleRevoke = async () => {
    try {
      await API.patch(`/admin/delegations/${revokeTarget.id}/revoke`);
      setShowRevokeConfirm(false);
      setRevokeTarget(null);
      fetchDelegations();
    } catch {}
  };

  const openCreate = () => {
    setError("");
    setForm({
      delegated_to_id: "",
      dsm_id: isDSM ? user.id : "",
      reason: "",
      expires_at: "",
    });
    setShowModal(true);
  };

  const fmt = (d) =>
    d
      ? new Date(d + (d.includes("T") ? "Z" : "")).toLocaleDateString("en-CA", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "—";

  return (
    <div>
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Delegations</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Grant temporary manager access to Account Sales Managers
          </p>
        </div>
        <button
          onClick={openCreate}
          className="px-4 py-2 rounded-lg text-sm font-medium text-white"
          style={{ backgroundColor: Color.NAVY }}
        >
          + Grant Delegation
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-16 text-gray-400">Loading...</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  ASM
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  Covering DSM
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">
                  Granted By
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">
                  Reason
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  Expires
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {delegations.map((d) => (
                <tr key={d.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-800">
                    {d.delegated_to_name}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{d.dsm_name}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs hidden md:table-cell">
                    {d.delegated_by_name}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs hidden md:table-cell">
                    {d.reason || "—"}
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-xs">
                    {fmt(d.expires_at)}
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      label={d.is_active ? "Active" : "Inactive"}
                      bg={d.is_active ? "#E8F5E9" : "#F5F5F5"}
                      color={d.is_active ? "#005700" : "#666"}
                    />
                  </td>
                  <td className="px-4 py-3">
                    {d.is_active && (
                      <button
                        onClick={() => confirmRevoke(d)}
                        className="px-3 py-1 rounded-lg text-xs font-medium border transition"
                        style={{ borderColor: Color.RED, color: Color.RED }}
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {delegations.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-10 text-center text-gray-400 text-sm"
                  >
                    No delegations yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Grant modal */}
      {showModal && (
        <Modal title="Grant Delegation" onClose={() => setShowModal(false)}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Delegate To (ASM) <span className="text-red-500">*</span>
              </label>
              <select
                className={selectCls}
                value={form.delegated_to_id}
                onChange={(e) =>
                  setForm((f) => ({ ...f, delegated_to_id: e.target.value }))
                }
              >
                <option value="">Select ASM...</option>
                {asms.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>

            {!isDSM && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Covering DSM <span className="text-red-500">*</span>
                </label>
                <select
                  className={selectCls}
                  value={form.dsm_id}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, dsm_id: e.target.value }))
                  }
                >
                  <option value="">Select DSM...</option>
                  {dsms.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expires On <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                className={inputCls}
                value={form.expires_at}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e) =>
                  setForm((f) => ({ ...f, expires_at: e.target.value }))
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason (optional)
              </label>
              <input
                className={inputCls}
                placeholder="e.g. Vacation coverage"
                value={form.reason}
                onChange={(e) =>
                  setForm((f) => ({ ...f, reason: e.target.value }))
                }
              />
            </div>

            {error && (
              <div
                className="p-3 rounded-lg text-sm"
                style={{ backgroundColor: "#FDECEA", color: "#A00028" }}
              >
                {error}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 py-2.5 rounded-xl border border-gray-300 text-sm font-medium text-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={
                  !form.delegated_to_id ||
                  !form.expires_at ||
                  (!isDSM && !form.dsm_id)
                }
                className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: Color.NAVY }}
              >
                Grant Delegation
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Revoke confirmation modal */}
      {showRevokeConfirm && revokeTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.4)" }}
        >
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: "#FDECEA" }}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                style={{ color: Color.RED }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-gray-800 text-center mb-2">
              Revoke Delegation?
            </h3>
            <p className="text-sm text-gray-500 text-center mb-6">
              This will remove manager access for{" "}
              <strong>{revokeTarget.delegated_to_name}</strong>. They will be
              notified by email.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRevokeConfirm(false);
                  setRevokeTarget(null);
                }}
                className="flex-1 py-2.5 rounded-xl border border-gray-300 text-sm font-medium text-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleRevoke}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white"
                style={{ backgroundColor: Color.RED }}
              >
                Yes, Revoke
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
